import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopHeader from './TopHeader';
import CommandPalette from './CommandPalette';
import TopBarLoader from '../Reusable_Components/TopBarLoader';
import Footer from './Footer';

const PUBLIC_ROUTES = ['/login', '/forgot-password', '/reset-password'];

// Section-name breadcrumb crumbs link to that section's first sidebar item
const SECTION_LINKS = {
  'Lead Management':        '/contact/list',
  'Customer Management':    '/customer/interactions/list',
  'Opportunity Management': '/opportunity/stage/list',
  'Quote Management':       '/quote/list',
  'Marketing':              '/marketing/campaign/list',
  'Reporting':              '/reporting/overview',
  'Security & Audit':       '/security/audit-log/list',
  'Administrator':          '/administrator/tenant/list',
};

const getPageTitle = (pathname) => {
  const exact = {
    '/dashboard':                              'Dashboard',
    '/sla-alerts':                             'SLA & Escalation Alerts',
    '/notifications':                          'Notifications',
    '/reporting/overview':                     'Analytics Overview',
    '/login':                                  'Login',
    // Lead Management
    '/contact/list':                           'Contacts',
    '/lead/list':                              'Leads',
    '/tasks/list':                             'Tasks',
    '/opportunity/list':                       'Opportunities',
    '/workflow/list':                          'Workflows',
    '/customer/list':                          'Customers',
    '/lead/email-template/list':               'Email Templates',
    '/lead/sales-team/list':                   'Sales Teams',
    '/lead/sales-region/list':                 'Sales Regions',
    '/lead/sales-target/list':                 'Sales Targets',
    '/lead/sales-performance/list':            'Sales Performance',
    // Opportunity Management
    '/opportunity/stage/list':                 'Opportunity Stages',
    '/opportunity/activity/list':              'Opportunity Activities',
    '/opportunity/document/list':              'Opportunity Documents',
    '/opportunity/history/list':               'Opportunity History',
    '/opportunity/notification/list':          'Opportunity Notifications',
    '/opportunity/follow-up/list':             'Opportunity Follow-Ups',
    '/opportunity/note/list':                  'Opportunity Notes',
    '/opportunity/source/list':                'Opportunity Sources',
    '/opportunity/priority/list':              'Opportunity Priorities',
    '/opportunity/status/list':                'Opportunity Statuses',
    '/opportunity/type/list':                  'Opportunity Types',
    '/opportunity/category/list':              'Opportunity Categories',
    '/opportunity/subcategory/list':           'Opportunity Sub-Categories',
    '/opportunity/tag/list':                   'Opportunity Tags',
    '/opportunity/rating/list':                'Opportunity Ratings',
    '/opportunity/comment/list':               'Opportunity Comments',
    '/opportunity/task/list':                  'Opportunity Tasks',
    '/opportunity/event/list':                 'Opportunity Events',
    '/opportunity/meeting/list':               'Opportunity Meetings',
    '/opportunity/call/list':                  'Opportunity Calls',
    // Customer Management
    '/customer/interactions/list':             'Customer Interactions',
    '/customer/note/list':                     'Customer Notes',
    '/customer/activity/list':                 'Customer Activities',
    '/customer/activity-type/list':            'Activity Types',
    '/customer/communication-log/list':        'Communication Logs',
    '/customer/document/list':                 'Customer Documents',
    '/customer/reminder/list':                 'Reminders',
    '/customer/sales-stage/list':              'Sales Stages',
    '/customer/sales-status/list':             'Sales Statuses',
    '/customer/sales-forecast/list':           'Sales Forecasts',
    '/customer/webhook-config/list':           'Webhook Configs',
    '/customer/async-event-log/list':          'Async Event Logs',
    '/customer/gateway-message-log/list':      'Gateway Message Logs',
    '/customer/sync-log/list':                 'Customer Sync Logs',
    '/customer/change-log/list':               'Customer Change Logs',
    // Quote Management
    '/quote/list':                             'Quotes',
    '/quote/status/list':                      'Quote Statuses',
    '/quote/product/list':                     'Products',
    '/quote/line-item/list':                   'Quote Line Items',
    '/quote/version/list':                     'Quote Versions',
    '/quote/document/list':                    'Quote Documents',
    '/quote/task/list':                        'Quote Tasks',
    '/quote/follow-up/list':                   'Quote Follow-Ups',
    '/quote/payment/list':                     'Quote Payments',
    '/quote/approval/list':                    'Quote Approvals',
    '/quote/comment/list':                     'Quote Comments',
    // Reporting
    '/reporting/report/list':                  'Reports',
    '/reporting/report-field/list':            'Report Fields',
    '/reporting/filter/list':                  'Filters',
    '/reporting/scheduled-report/list':        'Scheduled Reports',
    '/reporting/segmentation/list':            'Segmentations',
    '/reporting/analytics/list':               'Analytics Data',
    '/reporting/trend/list':                   'Trend Analysis',
    '/reporting/forecast/list':                'Forecasts',
    // Security & Audit
    '/security/audit-log/list':                'Audit Logs',
    '/security/login-activity/list':           'Login Activity',
    '/security/access-control/list':           'Access Control',
    '/security/data-integrity/list':           'Data Integrity Checks',
    '/security/alert/list':                    'Security Alerts',
  };

  if (exact[pathname]) return exact[pathname];

  if (pathname.includes('/input')) {
    if (pathname.startsWith('/administrator/user'))              return 'User Details';
    if (pathname.startsWith('/contact'))                         return 'Contact Form';
    if (pathname.startsWith('/lead/email-template'))             return 'Email Template Form';
    if (pathname.startsWith('/lead/sales-team'))                 return 'Sales Team Form';
    if (pathname.startsWith('/lead/sales-region'))               return 'Sales Region Form';
    if (pathname.startsWith('/lead/sales-target'))               return 'Sales Target Form';
    if (pathname.startsWith('/lead/sales-performance'))          return 'Sales Performance Form';
    if (pathname.startsWith('/lead'))                            return 'Lead Form';
    if (pathname.startsWith('/task'))                            return 'Task Form';
    if (pathname.startsWith('/opportunity/stage'))               return 'Opportunity Stage Form';
    if (pathname.startsWith('/opportunity/activity'))            return 'Opportunity Activity Form';
    if (pathname.startsWith('/opportunity/document'))            return 'Opportunity Document Form';
    if (pathname.startsWith('/opportunity/user'))                return 'Assigned User Form';
    if (pathname.startsWith('/opportunity/follow-up'))           return 'Follow-Up Form';
    if (pathname.startsWith('/opportunity/note'))                return 'Opportunity Note Form';
    if (pathname.startsWith('/opportunity/source'))              return 'Opportunity Source Form';
    if (pathname.startsWith('/opportunity/priority'))            return 'Opportunity Priority Form';
    if (pathname.startsWith('/opportunity/status'))              return 'Opportunity Status Form';
    if (pathname.startsWith('/opportunity/type'))                return 'Opportunity Type Form';
    if (pathname.startsWith('/opportunity/category'))            return 'Opportunity Category Form';
    if (pathname.startsWith('/opportunity/subcategory'))         return 'Sub-Category Form';
    if (pathname.startsWith('/opportunity/tag'))                 return 'Opportunity Tag Form';
    if (pathname.startsWith('/opportunity/rating'))              return 'Opportunity Rating Form';
    if (pathname.startsWith('/opportunity/comment'))             return 'Opportunity Comment Form';
    if (pathname.startsWith('/opportunity/task'))                return 'Opportunity Task Form';
    if (pathname.startsWith('/opportunity/event'))               return 'Opportunity Event Form';
    if (pathname.startsWith('/opportunity/meeting'))             return 'Opportunity Meeting Form';
    if (pathname.startsWith('/opportunity/call'))                return 'Opportunity Call Form';
    if (pathname.startsWith('/opportunity'))                     return 'Opportunity Form';
    if (pathname.startsWith('/workflow'))                        return 'Workflow Form';
    if (pathname.startsWith('/customer/interaction'))            return 'Interaction Form';
    if (pathname.startsWith('/customer/note'))                   return 'Note Form';
    if (pathname.startsWith('/customer/activity-type'))          return 'Activity Type Form';
    if (pathname.startsWith('/customer/activity'))               return 'Activity Form';
    if (pathname.startsWith('/customer/communication-log'))      return 'Communication Log Form';
    if (pathname.startsWith('/customer/document'))               return 'Document Form';
    if (pathname.startsWith('/customer/reminder'))               return 'Reminder Form';
    if (pathname.startsWith('/customer/sales-stage'))            return 'Sales Stage Form';
    if (pathname.startsWith('/customer/sales-status'))           return 'Sales Status Form';
    if (pathname.startsWith('/customer/sales-forecast'))         return 'Sales Forecast Form';
    if (pathname.startsWith('/customer/webhook-config'))         return 'Webhook Config Form';
    if (pathname.startsWith('/customer/stage'))                  return 'Customer Form';
    if (pathname.startsWith('/quote/status'))                    return 'Quote Status Form';
    if (pathname.startsWith('/quote/product'))                   return 'Product Form';
    if (pathname.startsWith('/quote/line-item'))                 return 'Line Item Form';
    if (pathname.startsWith('/quote/version'))                   return 'Quote Version Form';
    if (pathname.startsWith('/quote/document'))                  return 'Quote Document Form';
    if (pathname.startsWith('/quote/task'))                      return 'Quote Task Form';
    if (pathname.startsWith('/quote/follow-up'))                 return 'Quote Follow-Up Form';
    if (pathname.startsWith('/quote/payment'))                   return 'Quote Payment Form';
    if (pathname.startsWith('/quote/approval'))                  return 'Quote Approval Form';
    if (pathname.startsWith('/quote/comment'))                   return 'Quote Comment Form';
    if (pathname.startsWith('/quote'))                           return 'Quote Form';
    if (pathname.startsWith('/reporting/report-field'))          return 'Report Field Form';
    if (pathname.startsWith('/reporting/filter'))                return 'Filter Form';
    if (pathname.startsWith('/reporting/scheduled-report'))      return 'Scheduled Report Form';
    if (pathname.startsWith('/reporting/segmentation'))          return 'Segmentation Form';
    if (pathname.startsWith('/reporting/analytics'))             return 'Analytics Data Form';
    if (pathname.startsWith('/reporting/trend'))                 return 'Trend Analysis Form';
    if (pathname.startsWith('/reporting/forecast'))              return 'Forecast Form';
    if (pathname.startsWith('/reporting/report'))                return 'Report Form';
    return 'New Record';
  }

  if (pathname.includes('/details')) {
    if (pathname.startsWith('/administrator/user'))              return 'User Details';
    if (pathname.startsWith('/contact'))                         return 'Contact Details';
    if (pathname.startsWith('/lead/email-template'))             return 'Email Template Details';
    if (pathname.startsWith('/lead/sales-team'))                 return 'Sales Team Details';
    if (pathname.startsWith('/lead/sales-region'))               return 'Sales Region Details';
    if (pathname.startsWith('/lead/sales-target'))               return 'Sales Target Details';
    if (pathname.startsWith('/lead/sales-performance'))          return 'Sales Performance Details';
    if (pathname.startsWith('/lead'))                            return 'Lead Details';
    if (pathname.startsWith('/task'))                            return 'Task Details';
    if (pathname.startsWith('/opportunity-stage'))               return 'Stage Details';
    if (pathname.startsWith('/opportunity-activity'))            return 'Activity Details';
    if (pathname.startsWith('/opportunity/note'))                return 'Opportunity Note Details';
    if (pathname.startsWith('/opportunity/source'))              return 'Opportunity Source Details';
    if (pathname.startsWith('/opportunity/priority'))            return 'Opportunity Priority Details';
    if (pathname.startsWith('/opportunity/status'))              return 'Opportunity Status Details';
    if (pathname.startsWith('/opportunity/type'))                return 'Opportunity Type Details';
    if (pathname.startsWith('/opportunity/category'))            return 'Opportunity Category Details';
    if (pathname.startsWith('/opportunity/subcategory'))         return 'Sub-Category Details';
    if (pathname.startsWith('/opportunity/tag'))                 return 'Opportunity Tag Details';
    if (pathname.startsWith('/opportunity/rating'))              return 'Opportunity Rating Details';
    if (pathname.startsWith('/opportunity/comment'))             return 'Opportunity Comment Details';
    if (pathname.startsWith('/opportunity/task'))                return 'Opportunity Task Details';
    if (pathname.startsWith('/opportunity/event'))               return 'Opportunity Event Details';
    if (pathname.startsWith('/opportunity/meeting'))             return 'Opportunity Meeting Details';
    if (pathname.startsWith('/opportunity/call'))                return 'Opportunity Call Details';
    if (pathname.startsWith('/opportunity'))                     return 'Opportunity Details';
    if (pathname.startsWith('/customer/interactions'))           return 'Interaction Details';
    if (pathname.startsWith('/customer/note'))                   return 'Note Details';
    if (pathname.startsWith('/customer/activity-type'))          return 'Activity Type Details';
    if (pathname.startsWith('/customer/activity'))               return 'Activity Details';
    if (pathname.startsWith('/customer/communication-log'))      return 'Communication Log Details';
    if (pathname.startsWith('/customer/document'))               return 'Document Details';
    if (pathname.startsWith('/customer/reminder'))               return 'Reminder Details';
    if (pathname.startsWith('/customer/sales-stage'))            return 'Sales Stage Details';
    if (pathname.startsWith('/customer/sales-status'))           return 'Sales Status Details';
    if (pathname.startsWith('/customer/sales-forecast'))         return 'Sales Forecast Details';
    if (pathname.startsWith('/customer/webhook-config'))         return 'Webhook Config Details';
    if (pathname.startsWith('/customer/async-event-log'))        return 'Event Log Details';
    if (pathname.startsWith('/customer/gateway-message-log'))    return 'Message Log Details';
    if (pathname.startsWith('/customer/sync-log'))               return 'Sync Log Details';
    if (pathname.startsWith('/customer/change-log'))             return 'Change Log Details';
    if (pathname.startsWith('/customer'))                        return 'Customer Details';
    if (pathname.startsWith('/workflow'))                        return 'Workflow Details';
    if (pathname.startsWith('/single/quote'))                    return 'Quote Details';
    if (pathname.startsWith('/quote/status'))                    return 'Quote Status Details';
    if (pathname.startsWith('/quote/product'))                   return 'Product Details';
    if (pathname.startsWith('/quote/line-item'))                 return 'Line Item Details';
    if (pathname.startsWith('/quote/version'))                   return 'Quote Version Details';
    if (pathname.startsWith('/quote/document'))                  return 'Quote Document Details';
    if (pathname.startsWith('/quote/task'))                      return 'Quote Task Details';
    if (pathname.startsWith('/quote/follow-up'))                 return 'Quote Follow-Up Details';
    if (pathname.startsWith('/quote/payment'))                   return 'Quote Payment Details';
    if (pathname.startsWith('/quote/approval'))                  return 'Quote Approval Details';
    if (pathname.startsWith('/reporting/report-field'))          return 'Report Field Details';
    if (pathname.startsWith('/reporting/filter'))                return 'Filter Details';
    if (pathname.startsWith('/reporting/scheduled-report'))      return 'Scheduled Report Details';
    if (pathname.startsWith('/reporting/segmentation'))          return 'Segmentation Details';
    if (pathname.startsWith('/reporting/analytics'))             return 'Analytics Details';
    if (pathname.startsWith('/reporting/trend'))                 return 'Trend Analysis Details';
    if (pathname.startsWith('/reporting/forecast'))              return 'Forecast Details';
    if (pathname.startsWith('/reporting/report'))                return 'Report Details';
    if (pathname.startsWith('/security/audit-log'))              return 'Audit Log Details';
    if (pathname.startsWith('/security/login-activity'))         return 'Login Activity Details';
    if (pathname.startsWith('/security/access-control'))         return 'Access Control Details';
    if (pathname.startsWith('/security/data-integrity'))         return 'Data Integrity Details';
    if (pathname.startsWith('/security/alert'))                  return 'Security Alert Details';
    return 'Details';
  }

  return null;
};

const getPageAction = (pathname) => {
  const mapping = {
    '/contact/list':               { label: 'Add Contact',          to: '/contact/input' },
    '/lead/list':                  { label: 'New Lead',             to: '/lead/input' },
    '/tasks/list':                 { label: 'New Task',             to: '/task/input' },
    '/opportunity/list':           { label: 'New Opportunity',      to: '/opportunity/input' },
    '/customer/list':              { label: 'New Customer',         to: '/customer/stage/input' },
    '/workflow/list':              { label: 'New Workflow',         to: '/workflow/input' },
    '/lead/email-template/list':   { label: 'New Email Template',   to: '/lead/email-template/input' },
    '/lead/sales-team/list':       { label: 'New Sales Team',       to: '/lead/sales-team/input' },
    '/lead/sales-region/list':     { label: 'New Sales Region',     to: '/lead/sales-region/input' },
    '/lead/sales-target/list':     { label: 'New Sales Target',     to: '/lead/sales-target/input' },
    '/lead/sales-performance/list':{ label: 'New Sales Performance',to: '/lead/sales-performance/input' },
    '/quote/list':                 { label: 'New Quote',            to: '/quote/input' },
    '/opportunity/stage/list':     { label: 'Add Stage',            to: '/opportunity/stage/input' },
    '/opportunity/activity/list':  { label: 'Add Activity',         to: '/opportunity/activity/input' },
    '/opportunity/document/list':  { label: 'Add Document',         to: '/opportunity/document/input' },
    '/opportunity/follow-up/list': { label: 'Add Follow-Up',        to: '/opportunity/follow-up/input' },
    '/opportunity/note/list':      { label: 'Add Note',             to: '/opportunity/note/input' },
    '/opportunity/comment/list':   { label: 'Add Comment',          to: '/opportunity/comment/input' },
    '/opportunity/task/list':      { label: 'Add Task',             to: '/opportunity/task/input' },
    '/opportunity/event/list':     { label: 'Add Event',            to: '/opportunity/event/input' },
    '/opportunity/meeting/list':   { label: 'Add Meeting',          to: '/opportunity/meeting/input' },
    '/opportunity/call/list':      { label: 'Add Call',             to: '/opportunity/call/input' },
    '/opportunity/source/list':    { label: 'Add Source',           to: '/opportunity/source/input' },
    '/opportunity/priority/list':  { label: 'Add Priority',         to: '/opportunity/priority/input' },
    '/opportunity/status/list':    { label: 'Add Status',           to: '/opportunity/status/input' },
    '/opportunity/type/list':      { label: 'Add Type',             to: '/opportunity/type/input' },
    '/opportunity/category/list':  { label: 'Add Category',         to: '/opportunity/category/input' },
    '/opportunity/subcategory/list':{ label: 'Add Sub-Category',    to: '/opportunity/subcategory/input' },
    '/opportunity/tag/list':       { label: 'Add Tag',              to: '/opportunity/tag/input' },
    '/opportunity/rating/list':    { label: 'Add Rating',           to: '/opportunity/rating/input' },
    '/customer/interactions/list': { label: 'Add Interaction',      to: '/customer/interaction/input' },
    '/customer/note/list':         { label: 'Add Note',             to: '/customer/note/input' },
    '/customer/activity/list':     { label: 'Add Activity',         to: '/customer/activity/input' },
    '/customer/activity-type/list':{ label: 'Add Activity Type',    to: '/customer/activity-type/input' },
    '/customer/communication-log/list': { label: 'Add Comm Log',    to: '/customer/communication-log/input' },
    '/customer/document/list':     { label: 'Add Document',         to: '/customer/document/input' },
    '/customer/reminder/list':     { label: 'Add Reminder',         to: '/customer/reminder/input' },
    '/customer/sales-stage/list':  { label: 'Add Sales Stage',      to: '/customer/sales-stage/input' },
    '/customer/sales-status/list': { label: 'Add Sales Status',      to: '/customer/sales-status/input' },
    '/customer/sales-forecast/list':{ label: 'Add Forecast',        to: '/customer/sales-forecast/input' },
    '/customer/webhook-config/list':{ label: 'Add Webhook Config',  to: '/customer/webhook-config/input' },
    '/quote/status/list':          { label: 'Add Quote Status',     to: '/quote/status/input' },
    '/quote/product/list':         { label: 'Add Product',          to: '/quote/product/input' },
    '/quote/line-item/list':       { label: 'Add Line Item',        to: '/quote/line-item/input' },
    '/quote/version/list':         { label: 'Add Version',          to: '/quote/version/input' },
    '/quote/document/list':        { label: 'Add Document',         to: '/quote/document/input' },
    '/quote/task/list':            { label: 'Add Quote Task',       to: '/quote/task/input' },
    '/quote/follow-up/list':       { label: 'Add Follow-Up',        to: '/quote/follow-up/input' },
    '/quote/payment/list':         { label: 'Add Payment',          to: '/quote/payment/input' },
    '/quote/approval/list':        { label: 'Add Approval',         to: '/quote/approval/input' },
    '/quote/comment/list':         { label: 'Add Comment',          to: '/quote/comment/input' },
    '/reporting/report/list':      { label: 'Add Report',           to: '/reporting/report/input' },
    '/reporting/report-field/list':{ label: 'Add Report Field',     to: '/reporting/report-field/input' },
    '/reporting/filter/list':      { label: 'Add Filter',           to: '/reporting/filter/input' },
    '/reporting/scheduled-report/list':{ label: 'Add Scheduled Report',to: '/reporting/scheduled-report/input' },
    '/reporting/segmentation/list':{ label: 'Add Segmentation',     to: '/reporting/segmentation/input' },
    '/reporting/analytics/list':   { label: 'Add Analytics Data',   to: '/reporting/analytics/input' },
    '/reporting/trend/list':       { label: 'Add Trend Analysis',   to: '/reporting/trend/input' },
    '/reporting/forecast/list':    { label: 'Add Forecast',         to: '/reporting/forecast/input' },
  };
  return mapping[pathname] || null;
};

const getBreadcrumbs = (pathname) => {
  const exact = {
    '/dashboard':                              ['Dashboard'],
    '/sla-alerts':                             ['General', 'SLA & Escalation Alerts'],
    '/notifications':                          ['General', 'Notifications'],
    '/login':                                  ['Auth', 'Login'],
    '/forgot-password':                        ['Auth', 'Forgot Password'],
    '/reset-password':                         ['Auth', 'Reset Password'],
    '/contact/list':                           ['Lead Management', 'Contacts'],
    '/lead/list':                              ['Lead Management', 'Leads'],
    '/tasks/list':                             ['Lead Management', 'Tasks'],
    '/opportunity/list':                       ['Lead Management', 'Opportunities'],
    '/workflow/list':                          ['Lead Management', 'Workflows'],
    '/customer/list':                          ['Lead Management', 'Customers'],
    '/lead/email-template/list':               ['Lead Management', 'Email Templates'],
    '/lead/sales-team/list':                   ['Lead Management', 'Sales Teams'],
    '/lead/sales-region/list':                 ['Lead Management', 'Sales Regions'],
    '/lead/sales-target/list':                 ['Lead Management', 'Sales Targets'],
    '/lead/sales-performance/list':            ['Lead Management', 'Sales Performance'],
    '/opportunity/stage/list':                 ['Opportunity Management', 'Opportunity Stages'],
    '/opportunity/activity/list':              ['Opportunity Management', 'Opportunity Activities'],
    '/opportunity/document/list':              ['Opportunity Management', 'Opportunity Documents'],
    '/opportunity/history/list':               ['Opportunity Management', 'Opportunity History'],
    '/opportunity/notification/list':          ['Opportunity Management', 'Opportunity Notifications'],
    '/opportunity/follow-up/list':             ['Opportunity Management', 'Opportunity Follow-Ups'],
    '/opportunity/note/list':                  ['Opportunity Management', 'Opportunity Notes'],
    '/opportunity/source/list':                ['Opportunity Management', 'Opportunity Sources'],
    '/opportunity/priority/list':              ['Opportunity Management', 'Opportunity Priorities'],
    '/opportunity/status/list':                ['Opportunity Management', 'Opportunity Statuses'],
    '/opportunity/type/list':                  ['Opportunity Management', 'Opportunity Types'],
    '/opportunity/category/list':              ['Opportunity Management', 'Opportunity Categories'],
    '/opportunity/subcategory/list':           ['Opportunity Management', 'Opportunity Sub-Categories'],
    '/opportunity/tag/list':                   ['Opportunity Management', 'Opportunity Tags'],
    '/opportunity/rating/list':                ['Opportunity Management', 'Opportunity Ratings'],
    '/opportunity/comment/list':               ['Opportunity Management', 'Opportunity Comments'],
    '/opportunity/task/list':                  ['Opportunity Management', 'Opportunity Tasks'],
    '/opportunity/event/list':                 ['Opportunity Management', 'Opportunity Events'],
    '/opportunity/meeting/list':               ['Opportunity Management', 'Opportunity Meetings'],
    '/opportunity/call/list':                  ['Opportunity Management', 'Opportunity Calls'],
    '/customer/interactions/list':             ['Customer Management', 'Customer Interactions'],
    '/customer/note/list':                     ['Customer Management', 'Customer Notes'],
    '/customer/activity/list':                 ['Customer Management', 'Customer Activities'],
    '/customer/activity-type/list':            ['Customer Management', 'Activity Types'],
    '/customer/communication-log/list':        ['Customer Management', 'Communication Logs'],
    '/customer/document/list':                 ['Customer Management', 'Customer Documents'],
    '/customer/reminder/list':                 ['Customer Management', 'Reminders'],
    '/customer/sales-stage/list':              ['Customer Management', 'Sales Stages'],
    '/customer/sales-status/list':             ['Customer Management', 'Sales Statuses'],
    '/customer/sales-forecast/list':           ['Customer Management', 'Sales Forecasts'],
    '/customer/webhook-config/list':           ['Customer Management', 'Webhook Configs'],
    '/customer/async-event-log/list':          ['Customer Management', 'Async Event Logs'],
    '/customer/gateway-message-log/list':      ['Customer Management', 'Gateway Message Logs'],
    '/customer/sync-log/list':                 ['Customer Management', 'Customer Sync Logs'],
    '/customer/change-log/list':               ['Customer Management', 'Customer Change Logs'],
    '/quote/list':                             ['Quote Management', 'Quotes'],
    '/quote/status/list':                      ['Quote Management', 'Quote Statuses'],
    '/quote/product/list':                     ['Quote Management', 'Products'],
    '/quote/line-item/list':                   ['Quote Management', 'Quote Line Items'],
    '/quote/version/list':                     ['Quote Management', 'Quote Versions'],
    '/quote/document/list':                    ['Quote Management', 'Quote Documents'],
    '/quote/task/list':                        ['Quote Management', 'Quote Tasks'],
    '/quote/follow-up/list':                   ['Quote Management', 'Quote Follow-Ups'],
    '/quote/payment/list':                     ['Quote Management', 'Quote Payments'],
    '/quote/approval/list':                    ['Quote Management', 'Quote Approvals'],
    '/quote/comment/list':                     ['Quote Management', 'Quote Comments'],
    '/reporting/report/list':                  ['Reporting', 'Reports'],
    '/reporting/report-field/list':            ['Reporting', 'Report Fields'],
    '/reporting/filter/list':                  ['Reporting', 'Filters'],
    '/reporting/scheduled-report/list':        ['Reporting', 'Scheduled Reports'],
    '/reporting/segmentation/list':            ['Reporting', 'Segmentations'],
    '/reporting/analytics/list':               ['Reporting', 'Analytics Data'],
    '/reporting/trend/list':                   ['Reporting', 'Trend Analysis'],
    '/reporting/forecast/list':                ['Reporting', 'Forecasts'],
    '/security/audit-log/list':                ['Security & Audit', 'Audit Logs'],
    '/security/login-activity/list':           ['Security & Audit', 'Login Activity'],
    '/security/access-control/list':           ['Security & Audit', 'Access Control'],
    '/security/data-integrity/list':           ['Security & Audit', 'Data Integrity Checks'],
    '/security/alert/list':                    ['Security & Audit', 'Security Alerts'],
  };

  const matched = exact[pathname];
  if (matched) return ['Home', ...matched];

  const title = getPageTitle(pathname);
  if (pathname.includes('/input')) {
    return ['Home', 'Forms', title || 'Input Form'];
  }
  if (pathname.includes('/details')) {
    return ['Home', 'Details', title || 'Details View'];
  }

  return ['Home', title || 'Page'];
};

const AppLayout = ({ children }) => {
  const { pathname } = useLocation();
  const isPublic = PUBLIC_ROUTES.includes(pathname);
  const token = localStorage.getItem('customer_management_token');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    localStorage.getItem('sidebar_collapsed') === 'true'
  );
  const [theme, setTheme] = useState(
    localStorage.getItem('app_theme') || 'dark'
  );
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');

  const openPalette = (q = '') => { setPaletteQuery(q); setPaletteOpen(true); };
  const closePalette = () => setPaletteOpen(false);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar_collapsed', String(next));
      return next;
    });
  };

  const toggleTheme = () => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('app_theme', next);
      return next;
    });
  };

  useEffect(() => {
    const label = getPageTitle(pathname);
    document.title = label ? `${label} | Katibu Customer Relations` : 'Katibu Customer Relations';
  }, [pathname]);

  // ── Scroll position persistence ──────────────────────────────
  const mainRef = useRef(null);

  // Restore saved scroll when the route changes
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(`scroll:${pathname}`);
    // Use rAF so the page content has rendered before we scroll
    const id = requestAnimationFrame(() => {
      el.scrollTop = saved ? parseInt(saved, 10) : 0;
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  // Save scroll position continuously while the user scrolls
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const onScroll = () => {
      sessionStorage.setItem(`scroll:${pathname}`, String(el.scrollTop));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [pathname]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        openPalette('');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (theme === 'light') {
      document.body.classList.add('light-theme');
    } else {
      document.body.classList.remove('light-theme');
    }
  }, [theme]);

  if (!isPublic && !token) return <Navigate to="/login" replace />;
  if (isPublic) return <>{children}<Footer fixed /></>;

  const crumbs = getBreadcrumbs(pathname);
  const title = getPageTitle(pathname);
  const action = getPageAction(pathname);

  return (
    <div className={`app-layout${sidebarCollapsed ? ' sidebar-collapsed' : ''}`}>
      <TopBarLoader />
      <CommandPalette open={paletteOpen} onClose={closePalette} initialQuery={paletteQuery} />
      <Sidebar />
      <div className="app-content">
        <TopHeader
          onToggleSidebar={toggleSidebar}
          theme={theme}
          onToggleTheme={toggleTheme}
          onOpenPalette={openPalette}
        />
        <main className="app-main" ref={mainRef}>
          {title && (
            <div className="page-header-wrap">
              <div className="page-header-breadcrumb">
                <Link to="/dashboard" className="breadcrumb-home">🏠 Home</Link>
                {crumbs.slice(1).map((crumb, idx, arr) => {
                  const isLast = idx === arr.length - 1;
                  const to = !isLast ? SECTION_LINKS[crumb] : null;
                  return (
                    <React.Fragment key={idx}>
                      <span className="breadcrumb-separator">›</span>
                      {to ? (
                        <Link to={to} className="breadcrumb-item breadcrumb-link">{crumb}</Link>
                      ) : (
                        <span className="breadcrumb-item" aria-current={isLast ? 'page' : undefined}>{crumb}</span>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="page-header-title-bar">
                <h1 className="page-header-title">{title}</h1>
                {action && (
                  <Link to={action.to} className="btn btn-primary">
                    {action.label.startsWith('+') ? action.label : `+ ${action.label}`}
                  </Link>
                )}
              </div>
            </div>
          )}
          {children}
        </main>
        <Footer />
      </div>
    </div>
  );
};

export default AppLayout;
